import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { API_ENDPOINT } from '../utils/api'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  profile: any | null
  signUp: (email: string, password: string, fullName?: string) => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<any | null>(null)
  const [loading, setLoading] = useState(true)

  // Helper function to load user profile
  const loadProfile = async () => {
    try {
      const response = await fetch(`${API_ENDPOINT}/profiles/me`, {
        method: 'GET',
        credentials: 'include', // Include cookies for authentication
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (response.ok) {
        const profileData = await response.json()
        setProfile(profileData)
      } else if (response.status === 404) {
        // Profile doesn't exist - this is handled by the backend
        console.log('Profile not found, backend will create one')
        setProfile(null)
      } else {
        console.error('Error loading profile:', response.status, await response.text())
        setProfile(null)
      }
    } catch (error) {
      console.error('Error loading profile:', error)
      setProfile(null)
    }
  }

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) {
        loadProfile()
        // Set cookies for existing session
        await setAuthCookies(session)
      }
      setLoading(false)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      
      if (session?.user) {
        loadProfile()
        // Set authentication cookies for seamless API access
        await setAuthCookies(session)
      } else {
        setProfile(null)
        // Clear cookies on logout
        if (event === 'SIGNED_OUT') {
          await clearAuthCookies()
        }
      }
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  // Helper function to set authentication cookies
  const setAuthCookies = async (session: any) => {
    if (!session?.access_token || !session?.refresh_token) {
      console.warn('Missing tokens for cookie authentication')
      return
    }

    try {
      const response = await fetch(`${API_ENDPOINT}/auth/set-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: allows cookies to be set
        body: JSON.stringify({
          access_token: session.access_token,
          refresh_token: session.refresh_token
        })
      })

      if (!response.ok) {
        console.error('Failed to set authentication cookies:', await response.text())
        return
      }

      const result = await response.json()
      console.log('✅ Authentication cookies set successfully:', result.message)
    } catch (error) {
      console.error('Error setting authentication cookies:', error)
    }
  }

  // Helper function to clear authentication cookies
  const clearAuthCookies = async () => {
    try {
      const response = await fetch(`${API_ENDPOINT}/auth/clear-session`, {
        method: 'POST',
        credentials: 'include'
      })

      if (!response.ok) {
        console.error('Failed to clear authentication cookies')
        return
      }

      console.log('✅ Authentication cookies cleared successfully')
    } catch (error) {
      console.error('Error clearing authentication cookies:', error)
    }
  }

  const signUp = async (email: string, password: string, fullName?: string) => {
    const { data, error } = await supabase.auth.signUp({ 
      email, 
      password,
      options: {
        data: {
          display_name: fullName
        }
      }
    })
    
    if (error) throw error
    
    // Profile will be created automatically by the backend when the user first accesses /profiles/me
    // We can optionally update it with the display name if provided
    if (data.user && fullName) {
      try {
        // Wait a moment for the auth state to propagate and cookies to be set
        setTimeout(async () => {
          try {
            await fetch(`${API_ENDPOINT}/profiles/me`, {
              method: 'PUT',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                display_name: fullName
              })
            })
          } catch (error) {
            console.error('Error updating profile after signup:', error)
          }
        }, 1000)
      } catch (error) {
        console.error('Error setting up profile update:', error)
        // Don't throw here - the signup was successful even if profile update failed
      }
    }
  }

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }

  const signOut = async () => {
    // Clear cookies first
    await clearAuthCookies()
    
    // Then sign out from Supabase
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      loading,
      signUp,
      signIn,
      signOut
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}